import { useCallback, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { repositoryResolver } from '@rammetec/data-access'
import {
  ModulesProvider,
  useSessionManager,
  useCurrentProfile,
  type ModuleResolver,
} from '@syscolect/application'
import { Icon } from '@rammetec/design-system'
import {
  buildModuleTree,
  flattenModuleTree,
  flattenToLeaves,
  grantsOf,
  resolveGrants,
  type Module,
  type ModuleGroup,
  type ModuleInstance,
  type OrganizationModule,
} from '@syscolect/domain'
import { type AppModule, defaultIcon } from './registry'
import { InstanceDisplayProvider, type InstanceDisplay } from './InstanceDisplayContext'

/**
 * Bridges the org module tree into `ModulesProvider` using the 0148 model:
 * 3 reads (module-groups + org modules + catalog) → resolveGrants → buildModuleTree.
 * Groups keep slug `'g'`; leaf `instances[].id` is always OrganizationModule.id.
 */
export function ModuleResolverBridge({ children }: { children: ReactNode }) {
  const { currentOrganizationId } = useSessionManager()
  const { profileData } = useCurrentProfile()

  const orgId = currentOrganizationId ?? undefined
  const profileId = profileData?.id
  const grants = profileData ? grantsOf(profileData) : undefined
  const flatModules = profileData?.flatModules === true

  const treeQuery = useQuery({
    queryKey: ['profileModuleTree', orgId, profileId, grants, flatModules],
    queryFn: async () => {
      if (!orgId || !grants || grants.length === 0) {
        return {
          modules: [],
          instances: [],
          instanceDisplay: new Map<string, InstanceDisplay>(),
        }
      }

      const [groupsRepo, orgModulesRepo, moduleRepo] = await Promise.all([
        repositoryResolver.resolveWithContext<ModuleGroup>('ModuleGroup', { orgId }),
        repositoryResolver.resolveWithContext<OrganizationModule>('OrganizationModule', { orgId }),
        repositoryResolver.resolve<Module>('Module'),
      ])

      const [groups, orgModules, catalog] = await Promise.all([
        groupsRepo.getAll(),
        orgModulesRepo.getAll(),
        moduleRepo.getAll(),
      ])

      const granted = resolveGrants(grants, groups, orgModules)
      const tree = buildModuleTree(groups, orgModules, catalog, granted, 'console')
      const roots = flatModules ? flattenToLeaves(tree) : tree
      return buildShellContracts(roots)
    },
    enabled: !!orgId && !!grants && grants.length > 0,
  })

  const { modules, instances, instanceDisplay } = treeQuery.data ?? {
    modules: [],
    instances: [],
    instanceDisplay: new Map<string, InstanceDisplay>(),
  }

  // True while module access is not yet knowable: profile still loading (no grants
  // yet) OR the tree query is actively fetching for the first time.
  const profile = useCurrentProfile()
  const isResolving =
    profile.isLoading ||
    (treeQuery.fetchStatus === 'fetching' && treeQuery.data === undefined)

  const resolver = useCallback<ModuleResolver<AppModule>>(
    () => ({ modules, instances }),
    [modules, instances],
  )

  return (
    <InstanceDisplayProvider value={instanceDisplay}>
      <ModulesProvider resolver={resolver} isResolving={isResolving}>{children}</ModulesProvider>
    </InstanceDisplayProvider>
  )
}

// ─── mapping helpers ──────────────────────────────────────────────────────────

function buildShellContracts(
  roots: ReturnType<typeof buildModuleTree>,
): {
  modules: AppModule[]
  instances: ModuleInstance[]
  instanceDisplay: Map<string, InstanceDisplay>
} {
  const modulesBySlug = new Map<string, AppModule>()
  const instances: ModuleInstance[] = []
  const instanceDisplay = new Map<string, InstanceDisplay>()

  for (const { node, parentMid } of flattenModuleTree(roots)) {
    const isGroup = node.kind === 'group'
    const slug = isGroup
      ? 'g'
      : (node.base.actionProperties?.redirectTo as string | undefined) ?? node.base.id

    const iconName = node.icon
    const description = node.description ?? ''
    const iconNode = iconName ? <Icon name={iconName} fallback={defaultIcon} /> : defaultIcon

    instanceDisplay.set(node.id, { icon: iconNode, description })

    if (!modulesBySlug.has(slug)) {
      const baseFields =
        node.kind === 'leaf'
          ? {
              action: node.orgModule.action ?? node.base.action,
              actionProperties:
                node.orgModule.actionProperties ?? node.base.actionProperties ?? {},
              description: node.description ?? node.base.description ?? '',
            }
          : {
              action: 'group',
              actionProperties: {},
              description: node.description ?? '',
            }

      modulesBySlug.set(slug, {
        id: node.id,
        name: node.name,
        ...baseFields,
        icon: iconName,
        slug,
        label: node.name,
        iconNode,
        kind: isGroup ? 'group' : 'leaf',
      })
    } else if (isGroup && modulesBySlug.get(slug)?.kind !== 'group') {
      modulesBySlug.set(slug, {
        id: node.id,
        name: node.name,
        description: node.description ?? '',
        action: 'group',
        actionProperties: {},
        icon: iconName,
        slug,
        label: node.name,
        iconNode,
        kind: 'group',
      })
    }

    instances.push({
      id: node.id,
      slug,
      label: node.name,
      parameters: {},
      parentMid,
    })
  }

  return { modules: [...modulesBySlug.values()], instances, instanceDisplay }
}
